import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { NgTemplateOutlet } from '@angular/common';
import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	OnDestroy,
	OnInit,
	TemplateRef,
	ViewChild,
	ViewContainerRef,
	ViewEncapsulation,
} from '@angular/core';
import {
	FormsModule,
	ReactiveFormsModule,
	UntypedFormGroup,
} from '@angular/forms';
import { MatButton, MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterLink } from '@angular/router';
import { RecordHistoryDto } from 'app/core/services/api.service';
import { ShortcutService } from 'app/core/services/shortcut.service';
import { Subject, takeUntil } from 'rxjs';
import { CamelCaseStringPipe } from '../../../shared/pipes/camel-case-string.pipe';

@Component({
	selector: 'shortcuts',
	templateUrl: './shortcuts.component.html',
	encapsulation: ViewEncapsulation.None,
	changeDetection: ChangeDetectionStrategy.OnPush,
	exportAs: 'shortcuts',
	imports: [
		MatButtonModule,
		MatIconModule,
		MatTooltipModule,
		NgTemplateOutlet,
		RouterLink,
		FormsModule,
		ReactiveFormsModule,
		MatFormFieldModule,
		MatInputModule,
		MatSlideToggleModule,
		CamelCaseStringPipe,
	],
})
export class ShortcutsComponent implements OnInit, OnDestroy {
	@ViewChild('shortcutsOrigin') private _shortcutsOrigin: MatButton;
	@ViewChild('shortcutsPanel') private _shortcutsPanel: TemplateRef<any>;

	mode: 'view' | 'modify' | 'add' | 'edit' = 'view';
	shortcutForm: UntypedFormGroup;
	shortcuts: RecordHistoryDto[];
	private _overlayRef: OverlayRef;
	private _unsubscribeAll: Subject<any> = new Subject<any>();

	/**
	 * Constructor
	 */
	constructor(
		private _changeDetectorRef: ChangeDetectorRef,
		private _shortcutsService: ShortcutService,
		private _overlay: Overlay,
		private _viewContainerRef: ViewContainerRef
	) {}

	// -----------------------------------------------------------------------------------------------------
	// @ Lifecycle hooks
	// -----------------------------------------------------------------------------------------------------

	/**
	 * On init
	 */
	ngOnInit(): void {
		// Get the shortcuts
		this._shortcutsService.getShortcuts
			.pipe(takeUntil(this._unsubscribeAll))
			.subscribe((shortcuts: RecordHistoryDto[]) => {
				// Load the shortcuts
				this.shortcuts = shortcuts;

				// Mark for check
				this._changeDetectorRef.markForCheck();
			});
	}

	/**
	 * On destroy
	 */
	ngOnDestroy(): void {
		// Unsubscribe from all subscriptions
		this._unsubscribeAll.next(null);
		this._unsubscribeAll.complete();

		// Dispose the overlay
		if (this._overlayRef) {
			this._overlayRef.dispose();
		}
	}

	// -----------------------------------------------------------------------------------------------------
	// @ Public methods
	// -----------------------------------------------------------------------------------------------------

	/**
	 * Open the shortcuts panel
	 */
	openPanel(): void {
		// Return if the shortcuts panel or its origin is not defined
		if (!this._shortcutsPanel || !this._shortcutsOrigin) {
			return;
		}

		// Make sure to start in 'view' mode
		this.mode = 'view';

		// Create the overlay if it doesn't exist
		if (!this._overlayRef) {
			this._createOverlay();
		}

		// Attach the portal to the overlay
		this._overlayRef.attach(
			new TemplatePortal(this._shortcutsPanel, this._viewContainerRef)
		);
	}

	/**
	 * Close the shortcuts panel
	 */
	closePanel(): void {
		this._overlayRef.detach();
	}

	/**
	 * Track by function for ngFor loops
	 *
	 * @param index
	 * @param item
	 */
	trackByFn(index: number, item: any): any {
		return item.id || index;
	}

	// -----------------------------------------------------------------------------------------------------
	// @ Private methods
	// -----------------------------------------------------------------------------------------------------

	/**
	 * Create the overlay
	 */
	private _createOverlay(): void {
		// Create the overlay
		this._overlayRef = this._overlay.create({
			hasBackdrop: true,
			backdropClass: 'fuse-backdrop-on-mobile',
			scrollStrategy: this._overlay.scrollStrategies.block(),
			positionStrategy: this._overlay
				.position()
				.flexibleConnectedTo(
					this._shortcutsOrigin._elementRef.nativeElement
				)
				.withLockedPosition(true)
				.withPush(true)
				.withPositions([
					{
						originX: 'start',
						originY: 'bottom',
						overlayX: 'start',
						overlayY: 'top',
					},
					{
						originX: 'start',
						originY: 'top',
						overlayX: 'start',
						overlayY: 'bottom',
					},
					{
						originX: 'end',
						originY: 'bottom',
						overlayX: 'end',
						overlayY: 'top',
					},
					{
						originX: 'end',
						originY: 'top',
						overlayX: 'end',
						overlayY: 'bottom',
					},
				]),
		});

		// Detach the overlay from the portal on backdrop click
		this._overlayRef.backdropClick().subscribe(() => {
			this._overlayRef.detach();
		});
	}
}
